import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid2,
	IconButton,
	Typography
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useFreelancerContext } from "../../../../../contexts/useFreelancerContext";
import {
	useDeleteFreelanceProfileProfessionalExperience,
	usePutFreelanceProfileProfessionalExperience
} from "../../../../../hooks/useFreelancerProfile";
import { FreelancerProfile } from "../../../../../models/FreelancerProfile";
import { UpdateFreelancerProfileProfessionalExperienceRequest } from "../../../../../models/requests/UpdateFreelancerProfileProfessionalExperienceRequest";
import {
	FreelancerOnboardingMyPresentationExperienceViewModel,
	FreelancerOnboardingMyPresentationStepViewModel
} from "../../../../../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import ExperienceDisplay from "./ExperienceDisplay";
import ExperienceForm from "./ExperienceForm";
import { saveProfessionalExperienceAsync } from "../../../../../api/freelancer-profiles-api";
import { LanguageCode } from "../../../../../models/enums/LanguageCode";
import { useTranslatableForm } from "../../../../../hooks/useTranslatableForm";
import { scheduleTranslationRefresh } from "../../../../../utils/translationRefresh";

interface ExperienceListContainerProps {
	readonly formik: FormikProps<FreelancerOnboardingMyPresentationStepViewModel>;
	readonly freelancerProfile: FreelancerProfile;
	readonly setExperienceEditMode: React.Dispatch<
		React.SetStateAction<{ [key: number]: boolean }>
	>;
	readonly experienceEditMode: { [key: number]: boolean };
	readonly setIsCityValueChangedOnLoadBecauseOfBeingTranslated: Dispatch<
		SetStateAction<boolean>
	>;
	readonly experienceTranslatableFormsRef?: React.MutableRefObject<Record<string, ReturnType<typeof useTranslatableForm> | null>>;
}

export default function ExperienceListContainer({
	formik,
	freelancerProfile,
	setExperienceEditMode,
	experienceEditMode,
	setIsCityValueChangedOnLoadBecauseOfBeingTranslated,
	experienceTranslatableFormsRef
}: ExperienceListContainerProps) {
	const { t, i18n } = useTranslation();
	const { newFreelancerProfileProfessionalExperience } = useFreelancerContext();
	const queryClient = useQueryClient();

	const putFreelanceProfileProfessionalExperience =
		usePutFreelanceProfileProfessionalExperience();
	const deleteFreelanceProfileProfessionalExperience =
		useDeleteFreelanceProfileProfessionalExperience();

	const formikRef = useRef(formik);
	formikRef.current = formik;

	// Store translatableForm refs for each experience (keyed by index)
	// Use the parent ref if provided, otherwise create a local one
	const localTranslatableFormsRef = useRef<Record<string, ReturnType<typeof useTranslatableForm> | null>>({});
	const translatableFormsRef = experienceTranslatableFormsRef ?? localTranslatableFormsRef;

	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [pendingCancelIndex, setPendingCancelIndex] = useState<number | null>(null);
	const [errorDialog, setErrorDialog] = useState<{
		open: boolean;
		title: string;
		message: string;
	}>({
		open: false,
		title: "",
		message: ""
	});
	const [expandedDescriptions, setExpandedDescriptions] = useState<{
		[key: number]: boolean;
	}>({});
	const [descriptionOverflows, setDescriptionOverflows] = useState<{
		[key: number]: boolean;
	}>({});
	const [disableConfirmButtonOnPendingSave, setDisableConfirmButtonOnPendingSave] =
		useState(false);

	// Refs for description elements
	const descriptionRefs = useRef<{ [key: number]: HTMLElement | null }>({});
	const overflowChecked = useRef<{ [key: number]: boolean }>({});

	// Refs for experience forms to enable scrolling to newly added experiences
	const experienceFormRefs = useRef<{ [key: number]: HTMLElement | null }>({});

	// Ids of experiences CREATED during a confirm whose per-language saves then failed. The formik
	// id is only assigned once the WHOLE confirm succeeded: assigning it earlier changes the list
	// key, remounts the ExperienceForm mid-confirm and wipes the translatable-form state — the
	// retry the error dialog promises would then silently save nothing. This ref turns the retry's
	// first call into an update of the already-created experience instead of a duplicate create.
	const createdExperienceIdsRef = useRef<{ [index: number]: number }>({});

	// Debounced update functions
	const debouncedFormikUpdate = useRef<{ [key: string]: any }>({});
	const textFieldHandlers = useRef<{
		[fieldPath: string]: {
			onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
			onBlur: () => void;
		};
	}>({});
	const autocompleteHandlers = useRef<{
		[fieldPath: string]: {
			onChange: (_: any, value: any) => void;
			onBlur: () => void;
		};
	}>({});
	const checkboxHandlers = useRef<{
		[fieldPath: string]: {
			onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
			onBlur: () => void;
		};
	}>({});

	// Initialize first experience if none exist
	useEffect(() => {
		if (formikRef.current.values.professionalExperiences.length > 0) return;

		formikRef.current.setFieldValue("professionalExperiences", [
			newFreelancerProfileProfessionalExperience
		]);
		setExperienceEditMode({ 0: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formikRef.current.initialValues.professionalExperiences]);

	// Helper functions
	const getDebouncedUpdate = useCallback((fieldPath: string) => {
		if (!debouncedFormikUpdate.current[fieldPath]) {
			let timeoutId: NodeJS.Timeout;
			debouncedFormikUpdate.current[fieldPath] = (value: string) => {
				clearTimeout(timeoutId);
				timeoutId = setTimeout(() => {
					formikRef.current.setFieldValue(fieldPath, value);
				}, 300);
			};
		}
		return debouncedFormikUpdate.current[fieldPath];
	}, []);

	const getFieldError = (fieldPath: string) => {
		const error = formik.errors;
		const touched = formik.touched;

		const pathParts = fieldPath.split(".");
		let currentError = error;
		let currentTouched = touched;

		for (const part of pathParts) {
			if (currentError && typeof currentError === "object") {
				currentError = (currentError as any)[part];
			}
			if (currentTouched && typeof currentTouched === "object") {
				currentTouched = (currentTouched as any)[part];
			}
		}

		if (!currentTouched || !currentError) {
			return "";
		}

		if (typeof currentError === "string") {
			return currentError;
		}

		if (Array.isArray(currentError)) {
			return String(currentError[0] ?? "");
		}

		return "";
	};

	const hasFieldError = (fieldPath: string) => {
		return Boolean(getFieldError(fieldPath));
	};

	const getTextFieldHandlers = useCallback(
		(fieldPath: string) => {
			if (!textFieldHandlers.current[fieldPath]) {
				textFieldHandlers.current[fieldPath] = {
					onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
						const value = event.target.value;
						getDebouncedUpdate(fieldPath)(value);
					},
					onBlur: () => {
						formikRef.current.setFieldTouched(fieldPath, true);
						setTimeout(() => {
							try {
								formikRef.current.validateField(fieldPath);
							} catch (error) {
								console.error("Validation error:", error);
							}
						}, 0);
					}
				};
			}
			return textFieldHandlers.current[fieldPath];
		},
		[getDebouncedUpdate]
	);

	const getAutocompleteHandlers = useCallback((fieldPath: string) => {
		if (!autocompleteHandlers.current[fieldPath]) {
			autocompleteHandlers.current[fieldPath] = {
				onChange: (_: any, value: any) => {
					formikRef.current.setFieldValue(fieldPath, value);
					setTimeout(() => {
						try {
							formikRef.current.validateField(fieldPath);
						} catch (error) {
							console.error("Validation error:", error);
						}
					}, 0);
				},
				onBlur: () => {
					formikRef.current.setFieldTouched(fieldPath, true);
					setTimeout(() => {
						formikRef.current.validateField(fieldPath).catch(console.error);
					}, 0);
				}
			};
		}
		return autocompleteHandlers.current[fieldPath];
	}, []);

	const getCheckboxHandlers = useCallback((fieldPath: string) => {
		if (!checkboxHandlers.current[fieldPath]) {
			checkboxHandlers.current[fieldPath] = {
				onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
					formikRef.current.setFieldValue(fieldPath, event.target.checked);
					setTimeout(() => {
						try {
							formikRef.current.validateField(fieldPath);
						} catch (error) {
							console.error("Validation error:", error);
						}
					}, 0);
				},
				onBlur: () => {
					formikRef.current.setFieldTouched(fieldPath, true);
					setTimeout(() => {
						try {
							formikRef.current.validateField(fieldPath);
						} catch (error) {
							console.error("Validation error:", error);
						}
					}, 0);
				}
			};
		}
		return checkboxHandlers.current[fieldPath];
	}, []);

	const getDatePickerHandlers = useCallback((fieldPath: string) => {
		return {
			onChange: (newValue: dayjs.Dayjs) => {
				const dateValue =
					newValue?.toString() !== "" && newValue?.isValid()
						? newValue.toISOString()
						: null;

				formikRef.current.setFieldValue(fieldPath, dateValue);
				formikRef.current.setFieldTouched(fieldPath, true);

				setTimeout(() => {
					try {
						formikRef.current.validateField(fieldPath);
					} catch (error) {
						console.error("Validation error:", error);
					}
				}, 0);
			},
			onBlur: () => {
				formikRef.current.setFieldTouched(fieldPath, true);
				setTimeout(() => {
					try {
						formikRef.current.validateField(fieldPath);
					} catch (error) {
						console.error("Validation error:", error);
					}
				}, 0);
			}
		};
	}, []);

	// Experience management functions
	const hasExperienceInEditMode = useCallback(() => {
		return Object.values(experienceEditMode).some(isEditing => isEditing);
	}, [experienceEditMode]);

	const hasFilledFields = useCallback(
		(
			experience: FreelancerOnboardingMyPresentationExperienceViewModel,
			index: number
		) => {
			return (
				JSON.stringify(newFreelancerProfileProfessionalExperience) !==
					JSON.stringify(experience) &&
				JSON.stringify({
					...formikRef.current.initialValues.professionalExperiences[index],
					city: {
						...formikRef.current.initialValues.professionalExperiences[index].city,
						label: ""
					}
				} as FreelancerOnboardingMyPresentationExperienceViewModel) !==
					JSON.stringify({
						...experience,
						city: {
							...experience.city,
							label: ""
						}
					} as FreelancerOnboardingMyPresentationExperienceViewModel)
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const hasInitialValues = useCallback(
		(index: number) => {
			return formikRef.current.initialValues.professionalExperiences[index];
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const reindexStateObject = useCallback(
		<T,>(prevState: { [key: number]: T }, removedIndex: number) => {
			const newState: { [key: number]: T } = {};
			Object.keys(prevState).forEach(key => {
				const numKey = parseInt(key);
				if (numKey < removedIndex) {
					newState[numKey] = prevState[numKey];
				} else if (numKey > removedIndex) {
					newState[numKey - 1] = prevState[numKey];
				}
			});
			return newState;
		},
		[]
	);

	const addExperience = useCallback(() => {
		if (hasExperienceInEditMode()) {
			return;
		}

		const newIndex = formikRef.current.values.professionalExperiences.length;

		formikRef.current.setFieldValue("professionalExperiences", [
			...formikRef.current.values.professionalExperiences,
			newFreelancerProfileProfessionalExperience
		]);
		setExperienceEditMode(prev => ({ ...prev, [newIndex]: true }));

		// Scroll to the newly added experience form and focus on company field
		setTimeout(() => {
			const experienceElement = experienceFormRefs.current[newIndex];
			if (experienceElement) {
				// Scroll to the experience form
				const elementRect = experienceElement.getBoundingClientRect();
				const scrollTop = elementRect.top + window.pageYOffset - 100; // 100px offset for better visibility

				window.scrollTo({
					top: scrollTop,
					behavior: "smooth"
				});

				// Focus on the company name field after scrolling
				setTimeout(() => {
					// Find the company name input field within the experience form
					const companyInput = experienceElement.querySelector(
						`input[name="professionalExperiences.${newIndex}.companyName"]`
					) as HTMLInputElement;

					if (companyInput) {
						companyInput.focus();
						companyInput.select(); // Select all text if any exists
					}
				}, 500); // Wait for smooth scroll to complete
			}
		}, 100); // Small delay to ensure DOM is updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasExperienceInEditMode, newFreelancerProfileProfessionalExperience]);

	const removeExperience = useCallback(
		(index: number) => {
			const updatedExperiences = formikRef.current.values.professionalExperiences.filter(
				(_, i: number) => i !== index
			);
			formikRef.current.setFieldValue("professionalExperiences", updatedExperiences);
			setExperienceEditMode(prev => reindexStateObject(prev, index));

			// Clean up the experience form ref
			delete experienceFormRefs.current[index];
			// Reindex the remaining refs
			const newRefs: { [key: number]: HTMLElement | null } = {};
			Object.keys(experienceFormRefs.current).forEach(key => {
				const numKey = parseInt(key);
				if (numKey < index) {
					newRefs[numKey] = experienceFormRefs.current[numKey];
				} else if (numKey > index) {
					newRefs[numKey - 1] = experienceFormRefs.current[numKey];
				}
			});
			experienceFormRefs.current = newRefs;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[reindexStateObject]
	);

	const handleEditExperience = useCallback((index: number) => {
		setExperienceEditMode(prev => ({ ...prev, [index]: true }));
		setIsCityValueChangedOnLoadBecauseOfBeingTranslated(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleConfirmExperience = useCallback(
		async (index: number) => {
			const updatedProfessionalExperience =
				formikRef.current.values.professionalExperiences[index];

			// Translatable-form state of this experience (registered under its id, or its index
			// while it has not been created yet).
			const expKey = updatedProfessionalExperience.id != null
				? `id:${updatedProfessionalExperience.id}`
				: `idx:${index}`;
			const tf = translatableFormsRef.current[expKey];

			const hasText = (v?: string | null) => !!v && v.trim() !== "" && v !== "<p></p>";

			const buildRequest = (
				experienceId: typeof updatedProfessionalExperience.id,
				translatables: {
					jobTitle: string;
					missionDescription: string;
					results: string;
					technicalStack: string;
				}
			) =>
				({
					id: experienceId,
					freelancerProfileId: updatedProfessionalExperience.freelancerProfileId,
					isTopExperience: updatedProfessionalExperience.isTopExperience,
					cityId: updatedProfessionalExperience.city?.id ?? "",
					cityName: updatedProfessionalExperience.city?.label ?? "",
					companyName: updatedProfessionalExperience.companyName,
					sector: updatedProfessionalExperience.sector,
					jobTitle: translatables.jobTitle,
					jobType: updatedProfessionalExperience.jobType,
					startDate: updatedProfessionalExperience.startDate,
					endDate: updatedProfessionalExperience.endDate,
					isCurrentPosition: updatedProfessionalExperience.isCurrentPosition,
					jobLocation: updatedProfessionalExperience.jobLocation,
					missionDescription: translatables.missionDescription,
					results: translatables.results,
					technicalStack: translatables.technicalStack,
					skills: updatedProfessionalExperience.skills.map(
						ProfessionalExperienceSkill => ({
							id: ProfessionalExperienceSkill.id ?? 0,
							skillId: ProfessionalExperienceSkill.skill.id,
							freelancerProfileProfessionalExperienceId: experienceId
						})
					),
					businessDomains: updatedProfessionalExperience.businessDomains.map(
						ProfessionalExperienceBusinessDomain => ({
							id: ProfessionalExperienceBusinessDomain.id ?? 0,
							businessDomainId: ProfessionalExperienceBusinessDomain.businessDomain.id,
							freelancerProfileProfessionalExperienceId: experienceId
						})
					),
					documents: updatedProfessionalExperience.documents,
					urls: updatedProfessionalExperience.urls
				} as UpdateFreelancerProfileProfessionalExperienceRequest);

			// Create-flow = the formik row never received its id (first attempt OR retry after a
			// failed per-language save — the id is only assigned to formik once everything
			// succeeded). effectiveId turns a retry into an UPDATE of the already-created row.
			const isCreateFlow = updatedProfessionalExperience.id == null;
			const effectiveId =
				updatedProfessionalExperience.id ?? createdExperienceIdsRef.current[index];

			// FR translatable values from the HOOK, not the Formik. The FR primary field has two
			// sources of truth: the Formik (used to build this payload) and the translatable form
			// (what is displayed). Only the latter is refreshed when the worker re-translates the
			// FR (hand-back). Reading the Formik would resend a STALE FR and overwrite the fresh
			// AI cell as human — resurrecting an old value and freezing the field. The hook's value
			// is either identical to the cell (API skips it) or the genuine current FR.
			const frHookValues = tf ? tf.getValuesForLanguage(LanguageCode.Fr) : undefined;
			const frOf = (
				field: "jobTitle" | "missionDescription" | "results" | "technicalStack",
				formikValue?: string | null
			) => frHookValues?.[field] ?? formikValue ?? "";

			// An experience authored only in a non-primary language must be SAVED in that language:
			// the primary payload would carry empty required fields (formik only holds the FR
			// values). With languageCode, the API keeps the primary columns empty, stores the
			// values as human cells of that language and triggers the auto-translation.
			let createLanguage: LanguageCode | undefined;
			let createTranslatables = {
				jobTitle: frOf("jobTitle", updatedProfessionalExperience.jobTitle),
				missionDescription: frOf("missionDescription", updatedProfessionalExperience.missionDescription),
				results: frOf("results", updatedProfessionalExperience.results),
				technicalStack: frOf("technicalStack", updatedProfessionalExperience.technicalStack)
			};
			// Engage whenever FR does NOT hold BOTH required fields (not only when it holds
			// neither): with jobTitle in FR and missionDescription in EN, a primary create would
			// 400 — either another language holds both (create in it, FR follows as an update
			// below) or no language does (mixed dialog).
			if (
				isCreateFlow &&
				tf &&
				!(
					hasText(updatedProfessionalExperience.jobTitle) &&
					hasText(updatedProfessionalExperience.missionDescription)
				)
			) {
				// The create validator requires BOTH JobTitle and MissionDescription in the payload,
				// so the authored language must hold both.
				const authoredLang = tf
					.getModifiedLanguages()
					.map(({ languageCode }) => languageCode)
					.filter((languageCode) => languageCode !== LanguageCode.Fr)
					.find((languageCode) => {
						const langValues = tf.getValuesForLanguage(languageCode);
						return hasText(langValues.jobTitle) && hasText(langValues.missionDescription);
					});
				if (authoredLang) {
					const langValues = tf.getValuesForLanguage(authoredLang);
					createLanguage = authoredLang;
					createTranslatables = {
						jobTitle: langValues.jobTitle ?? "",
						missionDescription: langValues.missionDescription ?? "",
						results: langValues.results ?? "",
						technicalStack: langValues.technicalStack ?? ""
					};
				} else if (effectiveId === undefined) {
					// No single language holds both required fields (e.g. jobTitle typed in FR,
					// missionDescription in EN): the API cannot create from a mixed payload —
					// explain instead of letting the create 400 with a generic error.
					setErrorDialog({
						open: true,
						title: t("freelanceProfileProfessionalExperience.mixedLanguageCreateTitle"),
						message: t("freelanceProfileProfessionalExperience.mixedLanguageCreate")
					});
					return;
				}
			}

			try {
				const response = await putFreelanceProfileProfessionalExperience.mutateAsync({
					freelancerProfile,
					updateFreelancerProfileProfessionalExperienceRequest: buildRequest(
						effectiveId,
						createTranslatables
					),
					languageCode: createLanguage?.toLowerCase()
				});

				if (isCreateFlow && response.id) {
					// Remember the created id WITHOUT pushing it to formik yet: the formik id
					// changes the list key and remounts the form (wiping the translatable state)
					// — that must only happen after the per-language saves below succeeded.
					createdExperienceIdsRef.current[index] = response.id;
				}

				// Save the remaining non-primary language translations with the (possibly fresh) id.
				if (tf && response.id) {
					const modifiedLangs = tf.getModifiedLanguages();
					for (const { languageCode, fields } of modifiedLangs) {
						if (languageCode === LanguageCode.Fr) continue;
						if (createLanguage && languageCode === createLanguage) {
							// Already persisted by the non-primary CREATE above.
							tf.markLanguageSaved(languageCode);
							continue;
						}

						// Untouched fields carry the CURRENT value of this language, never "": an
						// empty value reads as a per-language CLEAR server-side, which 400s on the
						// required fields when no other human cell backs them (e.g. an AI-generated
						// FR cell for an EN-source talent). A genuinely cleared field still sends ""
						// and the API guard legitimately decides. That fallback needs the language's
						// cells to be LOADED — before that, "current value" would wrongly be "".
						const untouchedFieldExists = ["jobTitle", "missionDescription", "results", "technicalStack"]
							.some(field => fields[field] === undefined);
						if (untouchedFieldExists && !tf.translationsLoaded) {
							setErrorDialog({
								open: true,
								title: t("freelanceProfileProfessionalExperience.translationSaveErrorTitle"),
								message: t("freelanceProfileProfessionalExperience.translationSaveError")
							});
							setDisableConfirmButtonOnPendingSave(false);
							return;
						}
						const langValues = tf.getValuesForLanguage(languageCode);
						const valueOf = (field: string) =>
							fields[field] !== undefined ? fields[field] : langValues[field] ?? "";

						try {
							await saveProfessionalExperienceAsync(
								freelancerProfile.id,
								buildRequest(response.id, {
									jobTitle: valueOf("jobTitle"),
									missionDescription: valueOf("missionDescription"),
									results: valueOf("results"),
									technicalStack: valueOf("technicalStack")
								}),
								languageCode.toLowerCase()
							);
							tf.markLanguageSaved(languageCode);
						} catch (translationError) {
							// The primary save already succeeded (and showed its success snackbar):
							// surface THIS failure explicitly and keep the edit form open for a retry
							// — a console.error-only failure silently drops the user's translation.
							// The formik id was NOT set, so the form (and its translatable state)
							// stays mounted: the retry re-sends these very edits as an update.
							console.error("Error saving experience translations:", translationError);
							setErrorDialog({
								open: true,
								title: t("freelanceProfileProfessionalExperience.translationSaveErrorTitle"),
								message: t("freelanceProfileProfessionalExperience.translationSaveError")
							});
							setDisableConfirmButtonOnPendingSave(false);
							return;
						}
					}

					// A non-primary CREATE only carried that language: FR content typed in the
					// other fields (e.g. results in FR while authoring in EN) must follow as a
					// primary update, or it would silently vanish while FR gets marked saved.
					if (createLanguage) {
						const frHasContent =
							hasText(updatedProfessionalExperience.jobTitle) ||
							hasText(updatedProfessionalExperience.missionDescription) ||
							hasText(updatedProfessionalExperience.results) ||
							hasText(updatedProfessionalExperience.technicalStack);
						if (frHasContent) {
							try {
								await saveProfessionalExperienceAsync(
									freelancerProfile.id,
									buildRequest(response.id, {
										jobTitle: updatedProfessionalExperience.jobTitle,
										missionDescription: updatedProfessionalExperience.missionDescription,
										results: updatedProfessionalExperience.results ?? "",
										technicalStack: updatedProfessionalExperience.technicalStack ?? ""
									})
								);
							} catch (translationError) {
								console.error("Error saving primary-language content:", translationError);
								setErrorDialog({
									open: true,
									title: t("freelanceProfileProfessionalExperience.translationSaveErrorTitle"),
									message: t("freelanceProfileProfessionalExperience.translationSaveError")
								});
								setDisableConfirmButtonOnPendingSave(false);
								return;
							}
						}
					}

					// FR edits were persisted by the primary save (cells mirrored server-side).
					tf.markLanguageSaved(LanguageCode.Fr);
				}

				// Everything persisted: NOW the formik row may receive its id (this changes the
				// list key and remounts the form — safe here, the fresh instance re-fetches the
				// just-saved cells from the server).
				formikRef.current.setFieldValue(
					`professionalExperiences.${index}.id`,
					response.id
				);
				delete createdExperienceIdsRef.current[index];

				// Staggered refresh instead of a single invalidation: the worker translates
				// ASYNCHRONOUSLY, so an immediate refetch lands before the translated cells exist
				// and the user had to reload the page to see them. Scoped to the saved entity.
				scheduleTranslationRefresh(
					queryClient, "FreelancerProfileProfessionalExperience", response.id);

				setDisableConfirmButtonOnPendingSave(false);
				setExperienceEditMode(prev => ({ ...prev, [index]: false }));
			} catch (error) {
				setDisableConfirmButtonOnPendingSave(false);
				console.error("Error saving experience:", error);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formik]
	);

	const handleCancelExperience = useCallback(
		(index: number) => {
			const currentExperience = formikRef.current.values.professionalExperiences[index];

			if (hasFilledFields(currentExperience, index)) {
				setPendingCancelIndex(index);
				setConfirmDialogOpen(true);
			} else if (!freelancerProfile.professionalExperiences?.[index]) {
				removeExperience(index);
			} else {
				setExperienceEditMode(prev => ({ ...prev, [index]: false }));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[freelancerProfile]
	);

	const handleDeleteExperience = useCallback(
		async (index: number) => {
			try {
				await deleteFreelanceProfileProfessionalExperience.mutateAsync({
					freelancerProfile: freelancerProfile,
					freelancerOnboardingMyPresentationExperienceViewModel:
						formikRef.current.values.professionalExperiences[index]
				});

				removeExperience(index);
			} catch (error) {
				console.error("Error deleting experience:", error);
			}
		},
		[removeExperience, deleteFreelanceProfileProfessionalExperience, freelancerProfile]
	);

	// Dialog functions
	const closeErrorDialog = useCallback(() => {
		setErrorDialog({ open: false, title: "", message: "" });
	}, []);

	const closeConfirmDialog = useCallback(() => {
		setConfirmDialogOpen(false);
		setPendingCancelIndex(null);
	}, []);

	// Description handling functions
	const toggleDescriptionExpansion = useCallback(
		(index: number) => {
			setExpandedDescriptions(prev => ({
				...prev,
				[index]: !prev[index]
			}));
		},
		[setExpandedDescriptions]
	);

	const checkDescriptionOverflow = useCallback(
		(index: number) => {
			const element = descriptionRefs.current[index];
			if (element) {
				const isOverflowing = element.scrollHeight > element.clientHeight;
				setDescriptionOverflows(prev => ({
					...prev,
					[index]: isOverflowing
				}));
			}
		},
		[setDescriptionOverflows]
	);

	const setDescriptionRef = useCallback(
		(index: number, el: HTMLElement | null) => {
			if (el && descriptionRefs.current[index] !== el) {
				descriptionRefs.current[index] = el;
				if (!overflowChecked.current[index]) {
					overflowChecked.current[index] = true;
					requestAnimationFrame(() => {
						checkDescriptionOverflow(index);
					});
				}
			}
		},
		[checkDescriptionOverflow]
	);

	// Experience form ref handler
	const setExperienceFormRef = useCallback((index: number, el: HTMLElement | null) => {
		experienceFormRefs.current[index] = el;
	}, []);

	const confirmCancelExperience = useCallback(() => {
		if (pendingCancelIndex === null) return;

		if (!hasInitialValues(pendingCancelIndex)) {
			removeExperience(pendingCancelIndex);

			setConfirmDialogOpen(false);
			setPendingCancelIndex(null);

			return;
		}

		const initialValues =
			formikRef.current.initialValues.professionalExperiences[pendingCancelIndex];

		formikRef.current.setFieldValue(
			`professionalExperiences.${pendingCancelIndex}`,
			initialValues
		);

		setExperienceEditMode(prev => ({ ...prev, [pendingCancelIndex]: false }));

		Object.keys(
			formikRef.current.values.professionalExperiences[pendingCancelIndex]
		).forEach(field => {
			formikRef.current.setFieldTouched(
				`professionalExperiences.${pendingCancelIndex}.${field}`,
				false,
				false
			);
		});

		setConfirmDialogOpen(false);
		setPendingCancelIndex(null);
	}, [pendingCancelIndex, hasInitialValues, removeExperience, setExperienceEditMode]);

	return (
		<LocalizationProvider
			dateAdapter={AdapterDayjs}
			adapterLocale={i18n.language === "fr" ? "fr" : "en"}
		>
			<Box sx={{ marginBottom: 4 }}>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					sx={{ marginBottom: 1 }}
				>
					<Typography variant="h3">
						{t("freelancerOnboardingMyPresentation.experiences.title")}
					</Typography>
					<IconButton
						onClick={addExperience}
						size="large"
						color="secondary"
						disabled={hasExperienceInEditMode()}
					>
						<AddBoxOutlinedIcon sx={{ fontSize: 35 }} />
					</IconButton>
				</Box>
				<Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
					{t("freelancerOnboardingMyPresentation.experiences.description")}
				</Typography>

				{formikRef.current.values.professionalExperiences?.map(
					(
						experience: FreelancerOnboardingMyPresentationExperienceViewModel,
						index: number
					) =>
						experienceEditMode[index] ? (
							<Grid2
								key={experience.id ?? index}
								ref={el => setExperienceFormRef(index, el)}
							>
								<ExperienceForm
									formik={formik}
									freelancerProfile={freelancerProfile}
									experience={experience}
									index={index}
									onConfirm={handleConfirmExperience}
									onCancel={handleCancelExperience}
									getTextFieldHandlers={getTextFieldHandlers}
									getAutocompleteHandlers={getAutocompleteHandlers}
									getCheckboxHandlers={getCheckboxHandlers}
									getDatePickerHandlers={getDatePickerHandlers}
									getDebouncedUpdate={getDebouncedUpdate}
									hasFieldError={hasFieldError}
									getFieldError={getFieldError}
									formikRef={formikRef}
									setErrorDialog={setErrorDialog}
									disableConfirmButtonOnPendingSave={disableConfirmButtonOnPendingSave}
									setIsCityValueChangedOnLoadBecauseOfBeingTranslated={
										setIsCityValueChangedOnLoadBecauseOfBeingTranslated
									}
									onTranslatableFormRef={(ref) => {
										// Prefixed key — see experienceTranslatableFormsRef: a raw id/index
										// key lets a new experience collide with an existing one's DB id.
										const expKey = experience.id != null ? `id:${experience.id}` : `idx:${index}`;
										if (ref) {
											translatableFormsRef.current[expKey] = ref;
										} else {
											delete translatableFormsRef.current[expKey];
										}
									}}
								/>
							</Grid2>
						) : (
							<ExperienceDisplay
								key={experience.id ?? crypto.randomUUID()}
								experience={experience}
								index={index}
								onEdit={handleEditExperience}
								onDelete={handleDeleteExperience}
								expandedDescriptions={expandedDescriptions}
								descriptionOverflows={descriptionOverflows}
								onToggleDescription={toggleDescriptionExpansion}
								setDescriptionRef={setDescriptionRef}
							/>
						)
				)}

				{/* Confirmation Dialog */}
				<Dialog
					open={confirmDialogOpen}
					onClose={closeConfirmDialog}
					aria-labelledby="cancel-experience-dialog-title"
				>
					<DialogTitle id="cancel-experience-dialog-title">
						{t("freelancerOnboardingMyPresentation.experiences.confirmCancel.title")}
					</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{hasInitialValues(pendingCancelIndex ?? 0)
								? t(
										"freelancerOnboardingMyPresentation.experiences.confirmCancel.restoreMessage"
								  )
								: t(
										"freelancerOnboardingMyPresentation.experiences.confirmCancel.removeMessage"
								  )}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={closeConfirmDialog} color="secondary" variant="contained">
							{t(
								"freelancerOnboardingMyPresentation.experiences.confirmCancel.keepEditing"
							)}
						</Button>
						<Button
							onClick={confirmCancelExperience}
							color="secondary"
							variant="outlined"
							autoFocus
						>
							{t("freelancerOnboardingMyPresentation.experiences.confirmCancel.confirm")}
						</Button>
					</DialogActions>
				</Dialog>

				{/* Error Dialog */}
				<Dialog
					open={errorDialog.open}
					onClose={closeErrorDialog}
					aria-labelledby="error-dialog-title"
				>
					<DialogTitle id="error-dialog-title">{errorDialog.title}</DialogTitle>
					<DialogContent>
						<DialogContentText>{errorDialog.message}</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							sx={{ height: "35px" }}
							size="large"
							onClick={closeErrorDialog}
							color="secondary"
							variant="contained"
							autoFocus
						>
							{t("common.ok")}
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</LocalizationProvider>
	);
}
